import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import {
  SupervisorAccount as SupervisorAccountIcon,
  PersonOutlineOutlined as PersonOutlineOutlinedIcon,
} from "@mui/icons-material";

import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { ViewSettingsModal } from "../cohortViewSettings/ViewSettingsModal";
import { ScoringQuestionsModal } from "../cohortScoringQuestions/ScoringQuestionModal";
import { CohortPeriodsModal } from "../cohortPeriods/CohortPeriodsModal";
import { inputCommonStyle } from "../../../../util/admin/cssCommonStyle";
import AddButton from "../../../../containers/super-admin/add-button";
import { TimeZone, WeekEndDay } from "../../../../constants";
import { useQueryClient } from "@tanstack/react-query";
import { useKeycloak } from "@react-keycloak/web";
import { toast } from "sonner";
import { useFormik } from "formik";
import { useUpdateGroup } from "../../../../tanstackQuery/queries/superAdmin/group";
import { useAtom } from "jotai";
import { SAdminBaseAtom } from "../../../../stores/jotai/super-admin/superAdminBase";
import { useGetGroupPeriod } from "../../../../tanstackQuery/queries/superAdmin/group/period";
import { useParams } from "react-router-dom";

const items = [
  {
    id: "add-team",
    prefix: <SupervisorAccountIcon sx={{ marginRight: "10px" }} />,
    label: "Add Team",
    onClick: () => {},
  },
  {
    id: "add-user",
    label: "Add User",
    prefix: <PersonOutlineOutlinedIcon sx={{ marginRight: "10px" }} />,
    onClick: () => {},
  },
];

const CohortInfoCard = ({ cohortDetails }) => {
  const [sAdminBase]=useAtom(SAdminBaseAtom)
  const { keycloak } = useKeycloak();
  const client = useQueryClient();
  const {groupID} = useParams()
  const {
    mutateAsync: updateCohort,
    isPending,
    isSuccess,
  } = useUpdateGroup(
    { accessToken: keycloak?.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["orgtree"] });
        client.invalidateQueries({ queryKey: ["orgCohortDetails"] });
        client.invalidateQueries({ queryKey: ["group-period"] });
      },
    }
  );
  const { data: cohortPeriodData, isLoading: isCohortPeriodLoading } =
  useGetGroupPeriod(
    { accessToken: keycloak.token },
    {
      cohortID:groupID,
    },
    {
      enabled: !!groupID,
    }
  );
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: cohortDetails?.name || "",
      weekEndDay: cohortDetails?.weekEndDay || "Saturday",
      timeZone: cohortDetails?.timeZone || "PST",
      startDate: cohortDetails?.startDate ? dayjs(cohortDetails.startDate) : dayjs("2022-04-17"),
      endDate: cohortDetails?.endDate ? dayjs(cohortDetails.endDate) : dayjs("2022-04-17"),
      time: cohortDetails?.time ? dayjs(cohortDetails.time) : dayjs("2022-04-17T15:30"),
    },
    onSubmit: (values) => {
      handleSubmitCohortInfo(values);
    },
  });


  const handleSubmitCohortInfo = async (values) => {
     const updatedCohortData = {
        ...values,
        startDate: values.startDate.format("ddd, DD MMM YYYY HH:mm:ss [GMT]"),
        endDate: values.endDate.format("ddd, DD MMM YYYY HH:mm:ss [GMT]"),
        groupID:cohortDetails?.groupID,
        orgID:cohortDetails?.orgID,
     };
    try {
      await updateCohort(updatedCohortData);
      if (isSuccess) {
        toast.success("Cohort updated successfully!");
      }
    } catch (error) {
      formik.resetForm();
      toast.error(error?.response?.data?.message || "Failed to update Cohort.");
    }
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack
        direction="row"
        justifyContent="space-around"
        sx={{
          backgroundColor: "white",
          display: "flex",
          borderRadius: "4px",
          p: "16px 12px",
          my: 2,
        }}
      >
        <Stack direction="column" justifyContent="center" gap={2}>
          <Stack direction="row" gap={5} alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}> Cohort Name</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <TextField
                id="outlined-basic"
                variant="outlined"
                size="small"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                fullWidth
                sx={inputCommonStyle}
              />
            </Box>
          </Stack>
          <Stack direction="row" gap={5} alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold", textAlign: "center" }}>
                Start Date
              </Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <DatePicker
                value={formik.values.startDate}
                onChange={(newValue) => formik.setFieldValue("startDate", newValue)}
                sx={{
                  ".MuiInputBase-input": {
                    padding: "8px 8px",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>
          </Stack>
          <Stack direction="row" gap={5} alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold", textAlign: "center" }}>
                End Date
              </Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <DatePicker
                value={formik.values.endDate}
                onChange={(newValue) => formik.setFieldValue("endDate", newValue)}
                sx={{
                  ".MuiInputBase-input": {
                    padding: "8px 8px",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>
          </Stack>
        </Stack>

        <Stack direction="column" justifyContent="center" gap={2}>
          <Stack direction="row" gap={5} alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}> Reset Day</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <FormControl sx={inputCommonStyle} fullWidth>
                <Select
                  name="weekEndDay"
                  value={formik.values.weekEndDay}
                  onChange={formik.handleChange}
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  id="select"
                >
                  {
                    Object?.keys(WeekEndDay)?.map((value,index)=>(
                        <MenuItem key={index+value} value={value}>{value}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
          </Stack>
          <Stack direction="row" gap={5} alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}> Time Zone</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <FormControl sx={inputCommonStyle} fullWidth>
                <Select
                   name="timeZone"
                   value={formik.values.timeZone}
                   onChange={formik.handleChange}
                   displayEmpty
                   inputProps={{ "aria-label": "Without label" }}
                   id="select"
                >
                  {
                    Object.keys(TimeZone)?.map((value,index)=>(
                      <MenuItem key={index+value} value={value}>{value}</MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
          </Stack>
          <Stack direction="row" gap={5} alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Time</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <TimePicker
                value={formik.values.time}
                onChange={(newValue) => formik.setFieldValue("time", newValue)}
                sx={{
                  ".MuiInputBase-input": {
                    padding: "8px 8px",
                    fontSize: "14px",
                  },
                }}
              />
            </Box>
          </Stack>
        </Stack>

        <Stack direction="column" justifyContent="center" gap={2}>
          <Stack direction="row" gap={5} alignItems="center">
            <ViewSettingsModal cohortDetails={cohortDetails}/>
          </Stack>
          <Stack direction="row" gap={5} alignItems="center">
            <ScoringQuestionsModal />
          </Stack>
          <Stack direction="row" gap={5} alignItems="center">
          {
              !isCohortPeriodLoading && cohortPeriodData && <CohortPeriodsModal cohortPeriodData={cohortPeriodData}/>
          }
          </Stack>
        </Stack>

        <Stack direction="column" justifyContent="space-between" gap={2}>
          <Stack direction="row" gap={5} alignItems="center">
            <AddButton items={items} />
          </Stack>
          <Stack direction="row" gap={1}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                 backgroundColor: formik.dirty ? "#1976d2" : "#A5A5A5"
              }}
              disabled={!formik.dirty || isPending}
              endIcon={isPending ? <CircularProgress size={16} /> : null}
            >
              Update
            </Button>
            <Button fullWidth variant="contained">
              Delete
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
};

export default CohortInfoCard;
